import { Minus, Plus } from 'lucide-react';
import { useRef, useCallback, useState } from 'react';
import { cn } from '@/lib/utils';

interface MobileNumberStepperProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  unit?: string;
  className?: string;
  size?: 'default' | 'large';
}

export function MobileNumberStepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  label,
  unit,
  className,
  size = 'default',
}: MobileNumberStepperProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speedRef = useRef(100); // ms between increments during long press
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const isActiveRef = useRef(false);

  const currentValue = value ?? min;

  const increment = useCallback(() => {
    const newValue = Math.min(max, Math.round((currentValue + step) * 100) / 100);
    onChange(newValue);
  }, [currentValue, max, step, onChange]);

  const decrement = useCallback(() => {
    const newValue = Math.max(min, Math.round((currentValue - step) * 100) / 100);
    onChange(newValue);
  }, [currentValue, min, step, onChange]);

  // Long press with acceleration
  const startRepeating = useCallback((action: () => void, e: React.PointerEvent) => {
    if (isActiveRef.current) return;
    isActiveRef.current = true;
    e.preventDefault();

    action(); // First action immediately
    speedRef.current = 150; // Start slow

    // Start repeating after delay
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        action();
        // Accelerate over time (faster after more holds)
        if (speedRef.current > 50) {
          speedRef.current -= 10;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(action, speedRef.current);
          }
        }
      }, speedRef.current);
    }, 400);
  }, []);

  const stopRepeating = useCallback(() => {
    isActiveRef.current = false;
    speedRef.current = 150;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    if (newInputValue === '') {
      onChange(null);
      return;
    }

    const parsed = parseFloat(newInputValue.replace(',', '.'));
    if (!isNaN(parsed)) {
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setInputValue(value !== null ? value.toString() : '');
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    setInputValue('');
  };

  const getDisplayValue = (): string => {
    if (isFocused) {
      return inputValue;
    }
    if (value === null) return '';
    if (step < 1) {
      return value.toFixed(1);
    }
    return value.toString();
  };

  const isLarge = size === 'large';

  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <label className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 text-center">
          {label}
        </label>
      )}

      <div className="flex items-center justify-center gap-2">
        {/* Decrement button - 44x44px minimum */}
        <button
          type="button"
          onPointerDown={(e) => startRepeating(decrement, e)}
          onPointerUp={stopRepeating}
          onPointerLeave={stopRepeating}
          onPointerCancel={stopRepeating}
          disabled={currentValue <= min}
          aria-label={`Vähennä ${step}`}
          className={cn(
            'flex items-center justify-center rounded-xl',
            'bg-gray-100 dark:bg-gray-700',
            'text-gray-700 dark:text-gray-300',
            'transition-all duration-150 select-none touch-manipulation',
            'hover:bg-gray-200 dark:hover:bg-gray-600',
            'active:scale-90 active:bg-gray-300 dark:active:bg-gray-500',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500',
            isLarge ? 'w-14 h-14' : 'w-12 h-12'
          )}
        >
          <Minus className={isLarge ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
        </button>

        {/* Value input - large centered number */}
        <div className="relative flex flex-col items-center">
          <input
            type="text"
            inputMode="decimal"
            value={getDisplayValue()}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            aria-label={label || 'Arvo'}
            className={cn(
              'text-center font-bold bg-transparent',
              'text-gray-900 dark:text-white',
              'focus:outline-hidden focus:bg-gray-50 dark:focus:bg-gray-800 rounded-lg',
              'transition-colors',
              isLarge
                ? 'w-24 h-16 text-4xl'
                : 'w-20 h-12 text-3xl'
            )}
          />
          {unit && (
            <span className="text-sm text-gray-500 dark:text-gray-400 -mt-1">
              {unit}
            </span>
          )}
        </div>

        {/* Increment button - 44x44px minimum */}
        <button
          type="button"
          onPointerDown={(e) => startRepeating(increment, e)}
          onPointerUp={stopRepeating}
          onPointerLeave={stopRepeating}
          onPointerCancel={stopRepeating}
          disabled={currentValue >= max}
          aria-label={`Lisää ${step}`}
          className={cn(
            'flex items-center justify-center rounded-xl',
            'bg-primary-100 dark:bg-primary-900/40',
            'text-primary-700 dark:text-primary-300',
            'transition-all duration-150 select-none touch-manipulation',
            'hover:bg-primary-200 dark:hover:bg-primary-900/60',
            'active:scale-90 active:bg-primary-300 dark:active:bg-primary-800',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500',
            isLarge ? 'w-14 h-14' : 'w-12 h-12'
          )}
        >
          <Plus className={isLarge ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
